/**
 * Admin Documentation CMS — edit every role-scoped handbook.
 *
 * Two-pane editor:
 *   left   = handbook list
 *   right  = markdown editor (textarea) + live preview tab + Save / Reset / Download
 *
 * Aesthetic: Modern / minimal — borrows the legal admin page's structure
 * so the platform looks consistent.
 */
import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { renderMarkdown } from "@/lib/markdown";
import { BookOpen, FileDown, Save, RotateCcw, Eye, Edit3, ShieldCheck, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";


/* -------------------------------------------------------------------------- */
/*                          Editor — keyed sub-component                       */
/* -------------------------------------------------------------------------- */
/**
 * Keyed on `${slug}::${updated_at}` so every doc switch or post-save reload
 * remounts this component — initial state derives cleanly from props, no
 * "set state in effect" anti-pattern.
 */
function HandbookEditor({ active, onSaved, onReset, onDownload, saving, resetting, downloading }) {
    const [draftTitle, setDraftTitle] = useState(active.title || "");
    const [draftSubtitle, setDraftSubtitle] = useState(active.subtitle || "");
    const [draftBody, setDraftBody] = useState(active.body_md || "");
    const [mode, setMode] = useState("edit");

    const dirty =
        draftTitle !== (active.title || "") ||
        draftSubtitle !== (active.subtitle || "") ||
        draftBody !== (active.body_md || "");

    const handleSave = () => onSaved({ title: draftTitle, subtitle: draftSubtitle, body_md: draftBody });

    return (
        <div className="p-6 space-y-4">
            {/* Toolbar */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="inline-flex items-center gap-2 border border-border rounded-sm overflow-hidden">
                    <button
                        type="button"
                        onClick={() => setMode("edit")}
                        data-testid="admin-docs-tab-edit"
                        className={`px-3 py-1.5 text-xs uppercase tracking-[0.18em] inline-flex items-center gap-1.5 ${mode === "edit" ? "bg-primary/[0.08] text-primary" : "text-muted-foreground hover:bg-muted"}`}
                    >
                        <Edit3 className="w-3 h-3" strokeWidth={1.5} /> Edit
                    </button>
                    <button
                        type="button"
                        onClick={() => setMode("preview")}
                        data-testid="admin-docs-tab-preview"
                        className={`px-3 py-1.5 text-xs uppercase tracking-[0.18em] inline-flex items-center gap-1.5 ${mode === "preview" ? "bg-primary/[0.08] text-primary" : "text-muted-foreground hover:bg-muted"}`}
                    >
                        <Eye className="w-3 h-3" strokeWidth={1.5} /> Preview
                    </button>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        onClick={onDownload}
                        disabled={downloading}
                        data-testid="admin-docs-download"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-[0.18em] border border-border text-muted-foreground hover:bg-muted disabled:opacity-50 rounded-sm"
                    >
                        {downloading ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" strokeWidth={1.5} />}
                        .docx
                    </button>
                    <button
                        onClick={onReset}
                        disabled={resetting || active.slug === "release-notes"}
                        title={active.slug === "release-notes" ? "Release notes is auto-managed — use 'Sync from codebase' above" : ""}
                        data-testid="admin-docs-reset"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-[0.18em] border border-border text-muted-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed rounded-sm"
                    >
                        {resetting ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" strokeWidth={1.5} />}
                        Reset
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving || !dirty}
                        data-testid="admin-docs-save"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-[0.18em] border border-primary text-primary hover:bg-primary/10 disabled:opacity-40 rounded-sm"
                    >
                        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" strokeWidth={1.5} />}
                        {saving ? "Saving" : dirty ? "Save changes" : "Saved"}
                    </button>
                </div>
            </div>

            {mode === "edit" && (
                <div className="space-y-4">
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground block mb-1.5">Title</label>
                        <input
                            type="text"
                            value={draftTitle}
                            onChange={e => setDraftTitle(e.target.value)}
                            data-testid="admin-docs-title"
                            className="w-full px-3 py-2 bg-background border border-border text-foreground text-sm rounded-sm focus:border-primary focus:outline-none"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground block mb-1.5">Subtitle</label>
                        <input
                            type="text"
                            value={draftSubtitle}
                            onChange={e => setDraftSubtitle(e.target.value)}
                            data-testid="admin-docs-subtitle"
                            className="w-full px-3 py-2 bg-background border border-border text-foreground text-sm rounded-sm focus:border-primary focus:outline-none"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground block mb-1.5">
                            Markdown body
                            <span className="ml-2 text-[#5A5A5A] normal-case tracking-normal">
                                · # heading · **bold** · - bullet · 1. ordered · `code` · --- divider
                            </span>
                        </label>
                        <textarea
                            value={draftBody}
                            onChange={e => setDraftBody(e.target.value)}
                            data-testid="admin-docs-body"
                            rows={28}
                            className="w-full px-3 py-2 bg-background border border-border text-foreground text-[13px] font-mono rounded-sm focus:border-primary focus:outline-none leading-relaxed"
                        />
                    </div>
                </div>
            )}

            {mode === "preview" && (
                <article className="border-t border-border pt-4">
                    <h2 className="text-2xl font-semibold text-foreground tracking-tight">{draftTitle}</h2>
                    {draftSubtitle && <p className="text-sm text-muted-foreground mt-2">{draftSubtitle}</p>}
                    <div className="mt-4 prose-invert max-w-none" data-testid="admin-docs-preview-body">
                        {renderMarkdown(draftBody)}
                    </div>
                </article>
            )}
        </div>
    );
}


/* -------------------------------------------------------------------------- */
/*                                Page shell                                   */
/* -------------------------------------------------------------------------- */
export default function AdminDocumentation() {
    const [pages, setPages] = useState([]);
    const [activeSlug, setActiveSlug] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [syncing, setSyncing] = useState(false);

    const loadAll = async (preferSlug) => {
        setLoading(true);
        try {
            const r = await api.get("/admin/docs/pages");
            const list = r.data || [];
            setPages(list);
            setActiveSlug(prev => {
                const target = preferSlug || prev;
                if (target && list.some(p => p.slug === target)) return target;
                return list[0]?.slug || null;
            });
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadAll(); }, []); // eslint-disable-line

    const active = pages.find(p => p.slug === activeSlug) || null;

    const save = async (payload) => {
        if (!active) return;
        setSaving(true);
        try {
            await api.patch(`/admin/docs/pages/${active.slug}`, payload);
            toast.success("Handbook saved");
            await loadAll(active.slug);
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setSaving(false);
        }
    };

    const reset = async () => {
        if (!active) return;
        if (!window.confirm("Reset this handbook to the Law OS default content? Your edits will be lost.")) return;
        setResetting(true);
        try {
            await api.post(`/admin/docs/pages/${active.slug}/reset`, {});
            toast.success("Handbook reset to default");
            await loadAll(active.slug);
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setResetting(false);
        }
    };

    const download = async () => {
        if (!active) return;
        setDownloading(true);
        try {
            const res = await api.get(`/admin/docs/pages/${active.slug}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `law-os-${active.slug}.docx`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setDownloading(false);
        }
    };

    const syncFromCodebase = async () => {
        setSyncing(true);
        try {
            const r = await api.post("/admin/docs/sync-from-codebase", {});
            if (r.data?.unchanged) {
                toast.message(`Release notes already up to date · ${r.data.section_count || 0} releases on file`);
            } else {
                toast.success(`Release notes synced · ${r.data?.section_count || 0} releases`);
            }
            await loadAll("release-notes");
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setSyncing(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-docs-page">
            <header className="border border-border bg-card px-6 py-5 rounded-sm">
                <div className="flex items-center gap-2 text-primary text-[10px] uppercase tracking-[0.22em] mb-2">
                    <BookOpen className="w-3.5 h-3.5" strokeWidth={1.5} />
                    Documentation CMS
                </div>
                <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-foreground">
                    Enterprise handbooks
                </h1>
                <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
                    Edit the live handbook your admins, firm owners, and team members see in-app.
                    Changes auto-flow into the matching Word (.docx) export — no rebuild needed.
                </p>
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-primary/30 text-primary text-[10px] uppercase tracking-[0.18em] rounded-sm bg-primary/[0.04] mt-4">
                    <ShieldCheck className="w-3 h-3" strokeWidth={1.5} />
                    ISO-Grade · Audit-logged
                </div>
                <div className="mt-5 flex items-center justify-between gap-3 flex-wrap border-t border-[#1F1F1F] pt-4">
                    <div className="text-[11px] text-muted-foreground max-w-xl">
                        <span className="text-foreground font-medium">Auto-sync the Release Notes handbook</span> from
                        the platform changelog (PRD.md). Runs on server boot and on every click here — every release
                        is rendered with version, title, and date.
                    </div>
                    <button
                        onClick={syncFromCodebase}
                        disabled={syncing}
                        data-testid="admin-docs-sync-codebase"
                        className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.18em] border border-primary text-primary hover:bg-primary/10 disabled:opacity-50 rounded-sm whitespace-nowrap"
                    >
                        {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} />}
                        {syncing ? "Syncing…" : "Sync from codebase"}
                    </button>
                </div>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-6">
                <aside className="border border-border bg-card rounded-sm overflow-hidden" data-testid="admin-docs-sidebar">
                    <div className="px-4 py-3 border-b border-border text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                        Handbooks
                    </div>
                    {loading && (
                        <div className="px-4 py-6 text-xs text-muted-foreground flex items-center gap-2">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
                        </div>
                    )}
                    {!loading && pages.map(p => (
                        <button
                            key={p.slug}
                            onClick={() => setActiveSlug(p.slug)}
                            data-testid={`admin-docs-item-${p.slug}`}
                            className={`w-full text-left px-4 py-3 border-b border-[#1F1F1F] last:border-b-0 transition-colors ${activeSlug === p.slug ? "bg-primary/[0.06]" : "hover:bg-muted"}`}
                        >
                            <div className={`text-sm font-medium ${activeSlug === p.slug ? "text-primary" : "text-foreground"}`}>
                                {p.title}
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-1">
                                {p.audience_label} {p.is_default ? "· default" : "· edited"}
                            </div>
                        </button>
                    ))}
                </aside>

                <section className="border border-border bg-card rounded-sm" data-testid="admin-docs-editor">
                    {!active && (
                        <div className="px-6 py-10 text-xs text-muted-foreground">Select a handbook.</div>
                    )}
                    {active && (
                        <HandbookEditor
                            key={`${active.slug}::${active.updated_at || ""}`}
                            active={active}
                            onSaved={save}
                            onReset={reset}
                            onDownload={download}
                            saving={saving}
                            resetting={resetting}
                            downloading={downloading}
                        />
                    )}
                </section>
            </div>
        </div>
    );
}
